"use client";

import React, { useState, useRef } from "react";
import LayoutRight from "../../layouts/LayoutRight";
import Dropdown from "@/templates/Admin/components/DropDown/Dropdown";
import { CloseIcon, UploadIcon } from "../../utils/icons";
import PopupGroupView from "./popupGroupView";
import PopupListSceneGroupVideo from "./popupListSceneGroupVideo";
import s3UploadService from "../../utils/services/s3upload";
import videoGroupService from "../../utils/services/videoGroup";
import { useIdPath } from "@/contexts/IdPathProvider";

interface ScenceGroupVideoProps {
  onClose: () => void;
  isSidebarOpen: boolean;
}

interface ScenceGroupFormData {
  groupName: string;
  descriptionVN: string;
  descriptionEN: string;
  videoFile: File | null;
  videoPath: string;
  webpFile: File | null;
  webpPath: string;
  thumbnailFile: File | null;
  thumbnailPath: string;
  volume: string;
  showVideo: boolean;
}

interface FormErrors {
  groupName?: string;
  video?: string;
  thumbnail?: string;
}

interface Group {
  id: number;
  thumbnail: string;
  title?: string;
  is_show: number;
  parent: string;
  index: number;
}

interface VideoGroup {
  id: number;
  group_id: number;
  link_video: string;
  link_video_webm: string;
  thumbnail: string;
  volume: number;
  type: string;
  is_show: number;
  created_by: number;
  created_at: string;
  updated_by: number;
  updated_at: string;
}

const ScenceGroupVideo: React.FC<ScenceGroupVideoProps> = ({
  onClose,
  isSidebarOpen,
}) => {
  const { idPath, namePath } = useIdPath();
  // State
  const [formData, setFormData] = useState<ScenceGroupFormData>({
    groupName: "Group Video Name",
    descriptionVN: "",
    descriptionEN: "",
    videoFile: null,
    videoPath: "",
    webpFile: null,
    webpPath: "",
    thumbnailFile: null,
    thumbnailPath: "",
    volume: "6",
    showVideo: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingWebp, setIsUploadingWebp] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeTab, setActiveTab] = useState<"vn" | "en">("vn");
  const [showGroupViewPopup, setShowGroupViewPopup] = useState(false);
  const [showVideoGroupListPopup, setShowVideoGroupListPopup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editingVideoGroup, setEditingVideoGroup] = useState<VideoGroup | null>(null);

  // Utility function to truncate long text
  const truncatePath = (path: string, maxLength: number = 35): string => {
    if (!path) return "";
    if (path.length <= maxLength) return path;

    const fileName = path.split("/").pop() || "";
    const directory = path.substring(0, path.lastIndexOf("/"));

    if (fileName.length >= maxLength - 3) {
      return "..." + fileName.slice(-(maxLength - 3));
    }

    const availableLength = maxLength - fileName.length - 4; // 4 for ".../"
    if (availableLength <= 0) {
      return ".../" + fileName;
    }

    const truncatedDirectory =
      directory.length > availableLength
        ? "..." + directory.slice(-(availableLength - 3))
        : directory;

    return truncatedDirectory + "/" + fileName;
  };

  // Volume options
  const volumeOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8"];

  // Handlers
  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, groupName: e.target.value }));
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (activeTab === "vn") {
      setFormData((prev) => ({ ...prev, descriptionVN: e.target.value }));
    } else {
      setFormData((prev) => ({ ...prev, descriptionEN: e.target.value }));
    }
  };

  const handleVideoPathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, videoPath: e.target.value }));
  };

  const handleWebpPathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, webpPath: e.target.value }));
  };

  const handleThumbnailPathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, thumbnailPath: e.target.value }));
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingVideo(true);
      try {
        const cleanPath = namePath ? namePath.replace(/admin\/?/, "") : "";
        const formattedPath = cleanPath ? (cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`) : '';
        const subfolder = `${formattedPath}upload/video`;
        const response = await s3UploadService.uploadToS3(idPath, { 
          body: { file }, 
          subfolder 
        }) as any;

        if (response?.data?.success && response.data.file_url) {
          setFormData((prev) => ({
            ...prev,
            videoFile: file,
            videoPath: response.data.file_url,
          }));
        } else {
          alert("Lỗi khi tải video lên server");
        }
      } catch (error) {
        console.error("Upload error:", error);
        alert("Đã có lỗi xảy ra khi tải video");
      } finally {
        setIsUploadingVideo(false);
      }
    }
  };

  const handleWebpUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingWebp(true);
      try {
        const cleanPath = namePath ? namePath.replace(/admin\/?/, "") : "";
        const formattedPath = cleanPath ? (cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`) : '';
        const subfolder = `${formattedPath}upload/video`;
        const response = await s3UploadService.uploadToS3(idPath, { 
          body: { file }, 
          subfolder 
        }) as any;

        if (response?.data?.success && response.data.file_url) {
          setFormData((prev) => ({
            ...prev,
            webpFile: file,
            webpPath: response.data.file_url,
          }));
        } else {
          alert("Lỗi khi tải file WebM lên server");
        }
      } catch (error) {
        console.error("Upload error:", error);
        alert("Đã có lỗi xảy ra khi tải file WebM");
      } finally {
        setIsUploadingWebp(false);
      }
    }
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploadingThumbnail(true);
      try {
        const cleanPath = namePath ? namePath.replace(/admin\/?/, "") : "";
        const formattedPath = cleanPath ? (cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`) : '';
        const subfolder = `${formattedPath}upload/image/thumbnail`;
        const response = await s3UploadService.uploadToS3(idPath, { 
          body: { file }, 
          subfolder 
        }) as any;

        if (response?.data?.success && response.data.file_url) {
          setFormData((prev) => ({
            ...prev,
            thumbnailFile: file,
            thumbnailPath: response.data.file_url,
          }));
        } else {
          alert("Lỗi khi tải ảnh lên server");
        }
      } catch (error) {
        console.error("Upload error:", error);
        alert("Đã có lỗi xảy ra khi tải ảnh");
      } finally {
        setIsUploadingThumbnail(false);
      }
    }
  };

  const handleVolumeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, volume: value }));
  };

  const handleShowVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, showVideo: e.target.checked }));
  };

  const handleSwitchTab = (tab: "vn" | "en") => {
    setActiveTab(tab);
  };

  const handleGroupViewClick = () => {
    setShowGroupViewPopup(true);
  };

  const handleCloseGroupView = () => {
    setShowGroupViewPopup(false);
  };

  const handleVideoGroupListClick = () => {
    setShowVideoGroupListPopup(true);
  };

  const handleCloseVideoGroupList = () => {
    setShowVideoGroupListPopup(false);
  };

  const handleSelectGroup = (group: Group) => {
    setSelectedGroup(group);
    setShowGroupViewPopup(false);
    // You can add additional logic here to handle the selected group
  };

  const handleUpdateVideoGroup = (videoGroup: VideoGroup) => {
    setIsEdit(true);
    setEditingVideoGroup(videoGroup);
    setShowVideoGroupListPopup(false);
    
    // Fill form data with video group data
    setFormData({
      groupName: "Group Video Name", // Keep default or you can modify this
      descriptionVN: "",
      descriptionEN: "",
      videoFile: null,
      videoPath: videoGroup.link_video,
      webpFile: null,
      webpPath: videoGroup.link_video_webm,
      thumbnailFile: null,
      thumbnailPath: videoGroup.thumbnail,
      volume: videoGroup.volume.toString(),
      showVideo: videoGroup.is_show === 1,
    });

    // Set the selected group based on video group's group_id
    // We need to find the group from the groups list
    // For now, we'll create a temporary group object
    const tempGroup: Group = {
      id: videoGroup.group_id,
      thumbnail: "",
      title: `Group_${videoGroup.group_id}`, // Will be updated when groups are loaded
      is_show: 1,
      parent: "",
      index: 0
    };
    setSelectedGroup(tempGroup);
    
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.groupName.trim()) {
      newErrors.groupName = "Tên nhóm cảnh là bắt buộc";
    }

    if (!formData.videoFile && !formData.videoPath) {
      newErrors.video = "Vui lòng tải lên file video MP4";
    }

    // Optional: Add thumbnail validation if needed
    // if (!formData.thumbnailFile && !formData.thumbnailPath) {
    //   newErrors.thumbnail = "Vui lòng tải lên ảnh thumbnail";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Check if group is selected
    if (!selectedGroup) {
      alert('Vui lòng chọn một group trước khi tạo video group');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare video group data using paths already uploaded to S3
      const videoGroupBody = {
        group_id: selectedGroup.id,
        link_video: formData.videoPath,
        link_video_webm: formData.webpPath,
        thumbnail: formData.thumbnailPath,
        volume: parseInt(formData.volume),
        type: "mp4",
        is_show: formData.showVideo ? 1 : 0
      };

      let response;
      if (isEdit && editingVideoGroup) {
        // Update existing video group
        response = await videoGroupService.updateVideoGroup({ 
          body: videoGroupBody, 
          group_id: editingVideoGroup.id,
          idPath 
        });
      } else {
        // Create new video group
        response = await videoGroupService.createNewVideoGroup({ body: videoGroupBody, idPath }) as any;
      }
      
      if (response?.data?.success) {
        alert(isEdit ? 'Video group đã được cập nhật thành công!' : 'Video group đã được tạo thành công!');
        onClose();
      } else {
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} video group`);
      }

    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} video group:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Lỗi khi ${isEdit ? 'cập nhật' : 'tạo'} video group: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset edit state and form if in edit mode
    if (isEdit) {
      setIsEdit(false);
      setEditingVideoGroup(null);
      setSelectedGroup(null);
      
      // Reset form to default values
      setFormData({
        groupName: "Group Video Name",
        descriptionVN: "",
        descriptionEN: "",
        videoFile: null,
        videoPath: "",
        webpFile: null,
        webpPath: "",
        thumbnailFile: null,
        thumbnailPath: "",
        volume: "6",
        showVideo: true,
      });
      
      setErrors({});
    }
    onClose();
  };

  return (
    <LayoutRight isSidebarOpen={isSidebarOpen} onClose={onClose}>
      <div className="w-full h-full glass_background rounded-[16px] backdrop-blur-[100px] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.2),inset_-1px_1px_3px_0px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)] flex flex-col relative rounded-[20px] overflow-hidden">
        {/* Close button */}
        <div className="absolute top-2 right-2 p-2 flex items-center justify-center z-10">          <button
          className="bg-white rounded-[10px] w-10 h-10 flex items-center justify-center"
          onClick={onClose}
        >
          <CloseIcon />
        </button>
        </div>        {/* Scrollable Content */}
        <div className="flex-1 flex flex-col p-6 overflow-y-auto scrollbar-hide gap-7 pb-28">
          {/* Header */}
          <div className="flex flex-col gap-3 pt-6">
            <h1 className="text-2xl font-bold font-['Roboto_Flex'] leading-[1.171875] text-black">
              {isEdit ? "Chỉnh sửa video nhóm cảnh" : "Tạo video nhóm cảnh"}
            </h1>
          </div>

          {/* Group View Button */}
          <div className="flex flex-col gap-3 w-full">
            <div className="flex items-center gap-4">
              <button
                className="bg-[#5FCD6F] hover:bg-[#4db85c] text-white font-roboto text-base px-6 py-2 rounded-[6px] transition-colors duration-200 flex items-center gap-2"
                onClick={handleGroupViewClick}
              >
                Xem nhóm cảnh
              </button>
              <button
                className="bg-[#5FCD6F] hover:bg-[#4db85c] text-white font-roboto text-base px-6 py-2 rounded-[6px] transition-colors duration-200 flex items-center gap-2"
                onClick={handleVideoGroupListClick}
              >
                Danh sách video nhóm cảnh
              </button>
              {isEdit && (
                <button
                  className="bg-[#FF6B6B] hover:bg-[#ff5252] text-white font-roboto text-base px-6 py-2 rounded-[6px] transition-colors duration-200 flex items-center gap-2"
                  onClick={() => {
                    setIsEdit(false);
                    setEditingVideoGroup(null);
                    setSelectedGroup(null);
                    setFormData({
                      groupName: "Group Video Name",
                      descriptionVN: "",
                      descriptionEN: "",
                      videoFile: null,
                      videoPath: "",
                      webpFile: null,
                      webpPath: "",
                      thumbnailFile: null,
                      thumbnailPath: "",
                      volume: "6",
                      showVideo: true,
                    });
                    setErrors({});
                  }}
                >
                  Tạo mới
                </button>
              )}
            </div>
          </div>
          {selectedGroup && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-[6px]">
              <span className="text-sm font-medium text-blue-600">Đã chọn:</span>
              <span className="text-sm font-medium text-blue-800">{selectedGroup.title || `Group_${selectedGroup.id}`}</span>
              {isEdit && (
                <span className="text-xs text-blue-500 ml-2">(Có thể thay đổi bằng cách bấm Xem nhóm cảnh)</span>
              )}
            </div>
          )}
          {/* Thumbnails Upload */}
          <div className="flex flex-col gap-2">
            <label className="text-xl font-roboto text-black">
              Ảnh thu nhỏ (Thumbnail):
            </label>
            <div className="h-32 w-full rounded-[6px] bg-white border-2 border-dashed border-[#E0E0E0] flex items-center justify-center relative hover:border-[#5FCD6F] transition-colors duration-200">
              {isUploadingThumbnail ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-500">Đang tải lên...</span>
                </div>
              ) : formData.thumbnailFile ? (
                <div className="relative w-full h-full">
                  <img
                    src={URL.createObjectURL(formData.thumbnailFile)}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover rounded-[4px]"
                  />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, thumbnailFile: null, thumbnailPath: "" }))}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ) : formData.thumbnailPath && isEdit ? (
                <div className="relative w-full h-full">
                  <img
                    src={formData.thumbnailPath}
                    alt="Current thumbnail"
                    className="w-full h-full object-cover rounded-[4px]"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, thumbnailPath: "" }))}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-500">
                  <UploadIcon />
                  <span className="text-lg font-roboto">Kéo thả hoặc nhấp để tải ảnh lên</span>
                  <span className="text-sm">Hỗ trợ: JPG, PNG, WEBP</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                disabled={isUploadingThumbnail}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
            </div>
            {errors.thumbnail && (
              <span className="text-[#9F0000] text-sm">{errors.thumbnail}</span>
            )}
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-black"></div>

          {/* MP4 Video Upload */}
          <div className="flex flex-col gap-2">
            <label className="text-xl font-roboto text-black">
              Đường dẫn video (.mp4):
            </label>
            <div className="h-10 w-full rounded-[6px] bg-white border border-[#E0E0E0] flex items-center px-4 relative">
              <input
                type="text"
                value={isUploadingVideo ? "Đang tải lên..." : formData.videoPath}
                onChange={handleVideoPathChange}
                disabled={isUploadingVideo}
                className="text-xl font-roboto text-black flex-1 bg-transparent outline-none pr-10"
                placeholder="Nhập đường dẫn video (.mp4) hoặc tải lên..."
              />
              {/* Upload Button */}
              <label className="absolute right-0 w-10 h-10 flex items-center justify-center cursor-pointer">
                {isUploadingVideo ? (
                   <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <UploadIcon />
                )}
                <input
                  type="file"
                  accept=".mp4"
                  onChange={handleVideoUpload}
                  disabled={isUploadingVideo}
                  className="hidden"
                />
              </label>
            </div>
            {errors.video && (
              <span className="text-[#9F0000] text-sm">{errors.video}</span>
            )}
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-black"></div>

          {/* YouTube Link */}
          <div className="flex flex-col gap-2">
            <label className="text-xl font-roboto text-black">
              Đường dẫn YouTube:
            </label>
            <div className="h-10 w-full rounded-[6px] bg-white border border-[#E0E0E0] flex items-center px-4 relative">
              <input
                type="text"
                value={formData.webpPath}
                onChange={handleWebpPathChange}
                className="text-xl font-roboto text-black flex-1 bg-transparent outline-none"
                placeholder="Nhập đường dẫn YouTube..."
              />
            </div>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-black"></div>

          {/* Volume Section */}
          <div className="flex flex-col gap-2">
            <label className="text-xl font-roboto text-black">
              Âm lượng mặc định:
            </label>
            <div className="w-20">
              <Dropdown
                options={volumeOptions}
                value={formData.volume}
                onChange={handleVolumeChange}
                placeholder="6"
              />
            </div>
          </div>

          {/* Show Video Checkbox */}
          <div className="flex items-center gap-3 mt-4">
            <input
              type="checkbox"
              id="showVideo"
              checked={formData.showVideo}
              onChange={handleShowVideoChange}
              className="w-5 h-5 text-[#5FCD6F] bg-white border-2 border-[#E0E0E0] rounded focus:ring-[#5FCD6F] focus:ring-2"
            />
            <label
              htmlFor="showVideo"
              className="text-xl font-roboto text-black cursor-pointer select-none"
            >
              Hiển thị
            </label>
          </div>

          {/* Empty space for padding */}
          <div className="h-50 flex-shrink-0 pointer-events-none"></div>
        </div>

        {/* Fixed action buttons at bottom */}
        <div className="flex items-center gap-3 w-full px-6 py-5 bg-white/80 backdrop-blur-[60px] absolute bottom-0 left-0 shadow-[0_-4px_16px_0px_rgba(0,0,0,0.2),inset_-1px_1px_3px_0px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)] z-20">
          <button
            className="flex-grow flex justify-center items-center py-2 px-1 bg-[#5FCD6F] text-white rounded-[6px] text-lg"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isEdit ? "Lưu thay đổi" : "Tạo video nhóm cảnh"}
          </button>

          <button
            className="bg-[rgba(101,101,101,0.4)] text-white rounded-[6px] text-lg py-2 px-1 w-20 flex justify-center items-center font-roboto"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Hủy bỏ
          </button>
        </div>

        {/* Group View Popup */}
        {showGroupViewPopup && (
          <PopupGroupView
            onClose={handleCloseGroupView}
            onSelect={handleSelectGroup}
            language_id="en"
            idPath={idPath}
          />
        )}

        {/* Video Group List Popup */}
        {showVideoGroupListPopup && (
          <PopupListSceneGroupVideo
            onClose={handleCloseVideoGroupList}
            onUpdate={handleUpdateVideoGroup}
            language_id="en"
            idPath={idPath}
          />
        )}
      </div>
    </LayoutRight>
  );
};

export default ScenceGroupVideo;
